import { useCallback, useEffect, useRef, useState } from "react";
import { Patient, PatientEvent } from "../../../../services/patient.service";

export type ExistingImage = { id: number; imageUrl: string };
export type PhotoItem = File | ExistingImage;

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ACCEPTED_IMAGE_EXT = /\.(jpe?g|png|webp)$/i;

const EMPTY_PATIENT: Patient = {
  id: 0,
  name: "",
  phone: "",
  observation: "",
  consultationDate: "",
  returnDate: "",
  anamnese: "",
  events: [],
  tags: [],
};

type Args = {
  open: boolean;
  patient?: Patient;
  onSave: (patient: Patient & { photos?: File[]; removedImageIds?: number[] }) => void;
};

/** Estado e lógica do modal de paciente (abas, fotos, eventos, save). */
export function usePatientForm({ open, patient, onSave }: Args) {
  const [tabValue, setTabValue] = useState(0);
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [removedImageIds, setRemovedImageIds] = useState<number[]>([]);
  const [photoError, setPhotoError] = useState<string>("");
  const lastPatientRef = useRef<string>("");
  const [formData, setFormData] = useState<Patient>({ ...EMPTY_PATIENT });

  // Carrega/reseta o formulário ao abrir (ou ao trocar de paciente)
  useEffect(() => {
    if (!open) {
      lastPatientRef.current = "";
      return;
    }

    const patientJson = JSON.stringify(patient || null);
    if (patientJson === lastPatientRef.current) return;

    const isFirstLoad = lastPatientRef.current === "";
    lastPatientRef.current = patientJson;

    if (isFirstLoad) {
      setTabValue(0);
      setRemovedImageIds([]);
      setPhotos([]);
      setPhotoError("");
    }

    if (patient) {
      setFormData(patient);
      if (Array.isArray((patient as any).images)) {
        const serverImages: ExistingImage[] = (patient as any).images
          .filter((img: any) => img && img.id && img.imageUrl)
          .map((img: any) => ({ id: img.id, imageUrl: img.imageUrl }));
        setPhotos((prev) => {
          const userFiles = prev.filter((p): p is File => p instanceof File);
          return [...serverImages, ...userFiles];
        });
      }
    } else {
      setFormData({ ...EMPTY_PATIENT, tags: undefined as any });
      setPhotos([]);
      setRemovedImageIds([]);
    }
  }, [open, patient]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleTagsChange = useCallback((tags: string[]) => {
    setFormData((prev) => ({ ...prev, tags }));
  }, []);

  // Reaproveita um paciente já cadastrado com o mesmo nome em vez de criar um duplicado.
  const handleSelectExisting = useCallback((patient: Patient) => {
    setFormData(patient);
    setRemovedImageIds([]);
    setPhotoError("");
    const serverImages: ExistingImage[] = Array.isArray((patient as any).images)
      ? (patient as any).images.filter((img: any) => img && img.id && img.imageUrl).map((img: any) => ({ id: img.id, imageUrl: img.imageUrl }))
      : [];
    setPhotos(serverImages);
  }, []);

  const handleAddEvent = useCallback((event: PatientEvent) => {
    setFormData((prev) => ({ ...prev, events: [...(prev.events || []), event] }));
  }, []);

  const handleDeleteEvent = useCallback((index: number) => {
    setFormData((prev) => ({ ...prev, events: (prev.events || []).filter((_, i) => i !== index) }));
  }, []);

  const handlePhotoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const accepted: File[] = [];
      const rejected: string[] = [];
      Array.from(files).forEach((file) => {
        const isImage = ACCEPTED_IMAGE_TYPES.includes(file.type) || ACCEPTED_IMAGE_EXT.test(file.name);
        if (isImage) accepted.push(file);
        else rejected.push(file.name);
      });
      setPhotoError(
        rejected.length > 0
          ? `Formato não suportado: ${rejected.join(", ")}. Use imagens .jpg, .png ou .webp.`
          : ""
      );
      if (accepted.length > 0) setPhotos((prev) => [...prev, ...accepted]);
    }
    e.target.value = "";
  }, []);

  const handleRemovePhoto = useCallback((index: number) => {
    setPhotos((prev) => {
      const removed = prev[index];
      if (removed && !(removed instanceof File) && removed.id) {
        setRemovedImageIds((ids) => [...ids, removed.id]);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleSave = useCallback(() => {
    const newPhotos = photos.filter((p): p is File => p instanceof File);
    onSave({ ...formData, photos: newPhotos, removedImageIds: removedImageIds.length > 0 ? removedImageIds : undefined });
  }, [formData, photos, removedImageIds, onSave]);

  return {
    tabValue,
    setTabValue,
    photos,
    photoError,
    formData,
    handleChange,
    handleTagsChange,
    handleSelectExisting,
    handleAddEvent,
    handleDeleteEvent,
    handlePhotoChange,
    handleRemovePhoto,
    handleSave,
  };
}
